"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.zListWorkOrdersQuery = exports.zListWorkOrdersInvoiceStatus = exports.zAssignTechnicianRequest = exports.zUpdateSubletLineRequest = exports.zAddSubletLineRequest = exports.zUpdateLaborLineRequest = exports.zAddLaborLineRequest = exports.zUpdatePartLineRequest = exports.zAddPartLineRequest = exports.zVersionOnlyRequest = exports.zVoidWorkOrderRequest = exports.zUpdateInternalNotesRequest = exports.zUpdateWorkOrderRequest = exports.zCreateWorkOrderRequest = exports.zWorkOrderTechnicianRole = exports.WORK_ORDER_TECHNICIAN_ROLES = exports.zWorkOrderStatus = exports.WORK_ORDER_STATUSES = void 0;
const zod_1 = require("zod");
const inspections_js_1 = require("./inspections.js");
const money_js_1 = require("./money.js");
const query_params_js_1 = require("./query-params.js");
exports.WORK_ORDER_STATUSES = [
    "estimate",
    "approved",
    "in_progress",
    "complete",
    "invoiced",
    "void",
];
exports.zWorkOrderStatus = zod_1.z.enum(exports.WORK_ORDER_STATUSES);
exports.WORK_ORDER_TECHNICIAN_ROLES = ["primary", "assist", "inspector"];
exports.zWorkOrderTechnicianRole = zod_1.z.enum(exports.WORK_ORDER_TECHNICIAN_ROLES);
/**
 * Every mutation carries the aggregate's `version` and is rejected with a
 * 409 (never silently applied) if it doesn't match the CURRENT version —
 * see docs/IMPLEMENTATION_PLAN.md, "The non-obvious bug to design around":
 * two rapid edits to two DIFFERENT lines can otherwise race each other.
 */
const zVersioned = { version: zod_1.z.number().int().positive() };
exports.zCreateWorkOrderRequest = zod_1.z.object({
    customerId: zod_1.z.number().int().positive(),
    // Optional only for a `detailing`-shopType tenant (see
    // docs/SHOP_TYPES_PLAN.md) — WorkOrdersService.create() rejects an
    // omitted vehicleId for every other shop type, and otherwise silently
    // creates/reuses a placeholder vehicle for the customer (every field
    // null; there's no real vehicle profile to attach for that shop type).
    vehicleId: zod_1.z.number().int().positive().optional(),
    customerConcern: zod_1.z.string().trim().min(1).nullable().optional(),
    internalNotes: zod_1.z.string().trim().min(1).nullable().optional(),
    odometerIn: zod_1.z.number().int().min(0).nullable().optional(),
    primaryTechnicianId: zod_1.z.number().int().positive().nullable().optional(),
});
exports.zUpdateWorkOrderRequest = zod_1.z.object({
    ...zVersioned,
    customerConcern: zod_1.z.string().trim().min(1).nullable().optional(),
    internalNotes: zod_1.z.string().trim().min(1).nullable().optional(),
    // Printed on the estimate/invoice — distinct from customerConcern (the
    // intake "what's wrong"). See work_orders.customerNotes's own comment.
    customerNotes: zod_1.z.string().trim().min(1).nullable().optional(),
    odometerIn: zod_1.z.number().int().min(0).nullable().optional(),
    odometerOut: zod_1.z.number().int().min(0).nullable().optional(),
    primaryTechnicianId: zod_1.z.number().int().positive().nullable().optional(),
    payerName: zod_1.z.string().trim().min(1).nullable().optional(),
    claimNumber: zod_1.z.string().trim().min(1).nullable().optional(),
    authorizationNumber: zod_1.z.string().trim().min(1).nullable().optional(),
    // A single tenant-labeled free-text field — see tenant_settings.
    // flexField1Label's own doc comment and docs/SHOP_TYPES_PLAN.md.
    flexField1: zod_1.z.string().trim().min(1).nullable().optional(),
    // See PricingService.computeShopSupplies() — set to REPLACE the
    // rate-derived shop-supplies amount entirely; null clears the override
    // and reverts to the rate/cap-derived calculation.
    shopSuppliesOverrideAmount: money_js_1.zMoney.nullable().optional(),
    // Work-order-level discount, taken off the PRE-TAX subtotal — see
    // PricingService. `discountValue` is a percentage (0-100) when type is
    // "percent", a dollar amount when "amount". Send both null to clear.
    // (Type/value pairing and the 0-100 percent range are validated in
    // WorkOrdersService.update().)
    discountType: zod_1.z.enum(["percent", "amount"]).nullable().optional(),
    discountValue: money_js_1.zMoney.nullable().optional(),
    // The estimate strip's three EDITABLE fields (see WorkOrder below) — a
    // full revision trail is deferred for the MVP, so a scope change is
    // recorded here directly rather than through a new estimate document.
    // originalEstimateTotal is deliberately NOT editable here: it's set
    // once, by issuing the estimate (see EstimatesService), and never
    // changes afterward — that's what "original" means.
    currentEstimateTotal: money_js_1.zMoney.nullable().optional(),
    additionalCost: money_js_1.zMoney.nullable().optional(),
    revisedEstimateTotal: money_js_1.zMoney.nullable().optional(),
});
// A deliberate carve-out from the general `zUpdateWorkOrderRequest`/`update()`
// path, which rejects ANY change once a work order is invoiced (see
// WorkOrdersService.assertEditable) — the shop's own internal note is never
// printed/handed to the customer, so the owner asked for it to stay editable
// even after invoicing; see WorkOrdersService.updateInternalNotes(), which
// skips that lock entirely. customerNotes has no such carve-out (it prints
// on the invoice/estimate) and still goes through the regular, locked path.
exports.zUpdateInternalNotesRequest = zod_1.z.object({
    ...zVersioned,
    internalNotes: zod_1.z.string().nullable(),
});
exports.zVoidWorkOrderRequest = zod_1.z.object({ ...zVersioned, reason: zod_1.z.string().trim().min(1) });
exports.zVersionOnlyRequest = zod_1.z.object({ ...zVersioned });
exports.zAddPartLineRequest = zod_1.z.object({
    ...zVersioned,
    partNumber: zod_1.z.string().trim().min(1),
    description: zod_1.z.string().trim().min(1),
    quantity: money_js_1.zQty.default("1.000"),
    // Omitted means $0.00 — deliberately does NOT fall back to the catalog
    // part's current list price (the owner rejected that behavior).
    unitListPrice: money_js_1.zMoney.optional(),
    isTaxable: zod_1.z.boolean().optional(),
    // Checked (included in the Shop Supplies basis) by default — distinct
    // from isTaxable (sales tax).
    includeInShopSupplies: zod_1.z.boolean().optional(),
    // A state vehicle-inspection fee. The server forces tax-exempt + excluded from
    // shop supplies for these, whatever the two flags above say.
    isStateInspection: zod_1.z.boolean().optional(),
});
exports.zUpdatePartLineRequest = zod_1.z.object({
    ...zVersioned,
    partNumber: zod_1.z.string().trim().min(1).optional(),
    description: zod_1.z.string().trim().min(1).optional(),
    quantity: money_js_1.zQty.optional(),
    unitListPrice: money_js_1.zMoney.optional(),
    isTaxable: zod_1.z.boolean().optional(),
    includeInShopSupplies: zod_1.z.boolean().optional(),
    // Only meaningful on a state-inspection line: result / VIR # / inspector / date.
    inspection: inspections_js_1.zInspectionDetails.optional(),
});
function hasExtendedAmountOrHoursAndRate(data) {
    return data.extendedAmount != null || (data.hours != null && data.rate != null);
}
exports.zAddLaborLineRequest = zod_1.z
    .object({
    ...zVersioned,
    description: zod_1.z.string().trim().min(1),
    hours: money_js_1.zQty.nullable().optional(),
    rate: money_js_1.zMoney.nullable().optional(),
    // Authoritative — shops quote jobs, not hours (see db/schema/work-orders.ts).
    // Required unless BOTH hours and rate are given, in which case it's
    // derived as round2(hours * rate).
    extendedAmount: money_js_1.zMoney.optional(),
    // Omitted falls back to the shop's laborTaxRate (nonzero -> taxable
    // by default — see WorkOrdersService.defaultTaxableFor); an explicit
    // value here is how a per-line "Tax exempt" override sends `false`.
    isTaxable: zod_1.z.boolean().optional(),
    technicianId: zod_1.z.number().int().positive().nullable().optional(),
})
    .refine(hasExtendedAmountOrHoursAndRate, {
    message: "Provide extendedAmount, or both hours and rate.",
    path: ["extendedAmount"],
});
exports.zUpdateLaborLineRequest = zod_1.z.object({
    ...zVersioned,
    description: zod_1.z.string().trim().min(1).optional(),
    hours: money_js_1.zQty.nullable().optional(),
    rate: money_js_1.zMoney.nullable().optional(),
    extendedAmount: money_js_1.zMoney.optional(),
    isTaxable: zod_1.z.boolean().optional(),
    technicianId: zod_1.z.number().int().positive().nullable().optional(),
});
exports.zAddSubletLineRequest = zod_1.z.object({
    ...zVersioned,
    description: zod_1.z.string().trim().min(1),
    vendorName: zod_1.z.string().trim().min(1).nullable().optional(),
    costAmount: money_js_1.zMoney.nullable().optional(),
    priceAmount: money_js_1.zMoney,
    // See addLaborLine's own isTaxable comment above — same shape, keyed
    // off subletTaxRate instead.
    isTaxable: zod_1.z.boolean().optional(),
});
exports.zUpdateSubletLineRequest = zod_1.z.object({
    ...zVersioned,
    description: zod_1.z.string().trim().min(1).optional(),
    vendorName: zod_1.z.string().trim().min(1).nullable().optional(),
    costAmount: money_js_1.zMoney.nullable().optional(),
    priceAmount: money_js_1.zMoney.optional(),
    isTaxable: zod_1.z.boolean().optional(),
});
exports.zAssignTechnicianRequest = zod_1.z.object({
    ...zVersioned,
    technicianId: zod_1.z.number().int().positive(),
    role: exports.zWorkOrderTechnicianRole.default("primary"),
});
// Distinct from WorkOrderStatus: this filters on the linked invoice's own
// payment status (or its absence), a second, independent axis from the work
// order's own progress stage — see WorkOrderListItem's invoiceStatus.
exports.zListWorkOrdersInvoiceStatus = zod_1.z.enum(["not_invoiced", "open", "partially_paid", "paid", "void"]);
exports.zListWorkOrdersQuery = zod_1.z.object({
    // Multi-select: repeat `?status=`/`?invoiceStatus=` for each pill toggled
    // on, OR'd together — see zMultiValueQueryParam and WorkOrdersService.list().
    status: (0, query_params_js_1.zMultiValueQueryParam)(exports.zWorkOrderStatus),
    invoiceStatus: (0, query_params_js_1.zMultiValueQueryParam)(exports.zListWorkOrdersInvoiceStatus),
    customerId: zod_1.z.coerce.number().int().positive().optional(),
    vehicleId: zod_1.z.coerce.number().int().positive().optional(),
    technicianId: zod_1.z.coerce.number().int().positive().optional(),
    // Matches the customer's display name (join), an assigned technician's
    // name (join through work_order_technicians), or the work order's own
    // number (exact, if the search text is purely numeric).
    search: zod_1.z.string().trim().min(1).optional(),
    includeVoid: query_params_js_1.zBooleanQueryParam.default(false),
    // Column headers on the list page are clickable — see
    // WorkOrdersService.list()'s own whitelist mapping each of these to a
    // real ORDER BY expression. Defaults (unset) to the pre-existing
    // openedAt-desc order.
    sortBy: zod_1.z.enum(["openedAt", "workOrderNumber", "customerName", "status", "invoiceStatus", "total"]).optional(),
    sortDirection: zod_1.z.enum(["asc", "desc"]).default("desc"),
    limit: zod_1.z.coerce.number().int().min(1).max(100).default(25),
    offset: zod_1.z.coerce.number().int().min(0).default(0),
});
//# sourceMappingURL=work-orders.js.map